import React from 'react';
import { ApolloClient, ApolloConsumer, isApolloError } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import redirectTo from '@99designs/apps/spa/common/Ssr/redirectTo';
import { FullscreenError } from '@99designs/layout';
import { getApolloErrorType } from '@99designs/spa-common';

interface State {
    error?: Error;
}

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    client: ApolloClient<any>;
    children: React.ReactNode;
}

class ErrorBoundary extends React.Component<Props, State> {
    state = { error: undefined };

    static getDerivedStateFromError(error: Error) {
        // Update state so the next render will show the fallback UI.
        return { error: error };
    }

    render() {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        if (!isApolloError(error)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            Bugsnag.notify(error!);
            return <FullscreenError error={error} errorCode={'500'} />;
        }

        const type = getApolloErrorType(error);
        switch (type) {
            case 'UNAUTHENTICATED':
                redirectTo('/login', [{ key: 'from', value: window.location.href }]);
                return null;
            case 'NOT_FOUND':
                return <FullscreenError error={error} errorCode={'404'} />;
            case 'PERMISSION_DENIED':
                return <FullscreenError error={error} errorCode={'403'} />;
            default:
                return <FullscreenError error={error} errorCode={'500'} />;
        }
    }
}

export default function WrappedErrorBoundary({ children }: { children: React.ReactNode }) {
    return (
        <ApolloConsumer>
            {(client) => <ErrorBoundary client={client}>{children}</ErrorBoundary>}
        </ApolloConsumer>
    );
}
